/**
 * WebGL implementation of the Genesys renderer.
 * Extends THREE.WebGLRenderer and implements IGenesysRenderer interface.
 */

import * as THREE from 'three';
import { InstancedMesh } from 'three';


import { MaterialUtils } from '../materials.js';
import { CVarManager } from '../systems/ConsoleVariableManager.js';
import { ObjectTracker } from '../systems/ObjectTracker.js';
import { Stats } from '../systems/Stats.js';

import type { IGenesysRenderer } from './IGenesysRenderer.js';
import type { WebGPURenderer } from 'three/webgpu';


const cvarAutoInstancing = CVarManager.registerVariable({
  name: 'r.AutoInstancing',
  default: 0,
  description: 'Enable auto instancing on the GenesysRenderer',
});

type MeshInstances = Map<string, {
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  objects: THREE.Mesh[]
}>;

export class GenesysWebGLRenderer extends THREE.WebGLRenderer implements IGenesysRenderer {
  instanceMeshes: Map<string, { mesh: THREE.InstancedMesh, capacity: number }> = new Map();
  tempMatrix: THREE.Matrix4 = new THREE.Matrix4();
  tempColor: THREE.Color = new THREE.Color();
  minInstancingThreshold = 100;

  /**
   * Create and initialize a WebGL renderer.
   * @param options - WebGL renderer options
   * @returns Initialized GenesysWebGLRenderer
   */
  public static async create(options?: THREE.WebGLRendererParameters): Promise<GenesysWebGLRenderer> {
    const renderer = new GenesysWebGLRenderer(options);
    return renderer;
  }

  private constructor(options?: THREE.WebGLRendererParameters) {
    super(options);

    ObjectTracker.track(this);

    const originalRender = this.render.bind(this);
    this.render = (scene: THREE.Scene, camera: THREE.Camera) => {
      if (cvarAutoInstancing.get()) {
        const instances = this.autoInstance(scene);
        originalRender(scene, camera);
        // restore the visibility flag
        for (const { objects } of instances.values()) {
          for (const obj of objects) {
            obj.visible = true;
          }
        }
      } else {
        originalRender(scene, camera);
      }
    };

    const originalRenderBufferDirect = this.renderBufferDirect.bind(this);
    const warnedNodeMaterials = new Set<string>();
    this.renderBufferDirect = (camera: THREE.Camera, scene: THREE.Scene, geometry: THREE.BufferGeometry, material: THREE.Material, object: THREE.Object3D, geometryGroup: any) => {
      // NodeMaterials (WebGPU/TSL) have undefined vertexShader/fragmentShader and cannot be
      // compiled by the WebGL renderer — skip them to avoid a crash in resolveIncludes.
      if ((material as any).isNodeMaterial) {
        const key = material.type + ':' + object.name;
        if (!warnedNodeMaterials.has(key)) {
          warnedNodeMaterials.add(key);
          console.warn(`[GenesysWebGLRenderer] Skipping NodeMaterial "${material.type}" on object "${object.name}" — NodeMaterials require the WebGPU renderer.`);
        }
        return;
      }
      MaterialUtils.fixMaterialColorSpace(material);
      originalRenderBufferDirect(camera, scene, geometry, material, object, geometryGroup);
      Stats.addRenderBufferDirect(camera, scene, geometry, material, object, geometryGroup);
    };
  }

  public asWebGL(): THREE.WebGLRenderer | null {
    return this;
  }

  public asWebGPU(): WebGPURenderer | null {
    return null;
  }

  public disposeSceneResources(_scene: THREE.Scene): void {
    // WebGL renderer does not use common Renderer scene texture bindings.
  }

  @THREE.profile
  public beginRenderFrame(): void {
    for (const { mesh } of this.instanceMeshes.values()) {
      mesh.visible = false;
    }
  }

  @THREE.profile
  public endRenderFrame(): void {
    // remove instance meshes that are no longer needed
    for (const [key, { mesh }] of this.instanceMeshes.entries()) {
      if (!mesh.visible) {
        mesh.dispose();
        mesh.removeFromParent();
        this.instanceMeshes.delete(key);
      }
    }
  }

  private autoInstance(scene: THREE.Scene): MeshInstances {
    const instances: MeshInstances = new Map();

    // gather batchable instances
    scene.traverseVisible((obj: THREE.Object3D) => {
      if (!(obj instanceof THREE.Mesh)) return;
      if (obj instanceof THREE.SkinnedMesh) return;
      if (obj.userData.isAutoInstance) return;

      const key = `${obj.geometry.uuid}_${obj.material.uuid}_${obj.castShadow}_${obj.receiveShadow}`;

      if (!instances.has(key)) {
        instances.set(key, {
          geometry: obj.geometry,
          material: obj.material,
          objects: []
        });
      }
      instances.get(key)!.objects.push(obj);
    });

    // Create or update InstancedMesh for each group
    for (const [key, data] of instances) {
      let numInstances = 0;
      for (const obj of data.objects) {
        if (obj instanceof THREE.InstancedMesh) {
          numInstances += obj.count;
        } else {
          numInstances += 1;
        }
      }
      if (numInstances < this.minInstancingThreshold) continue;
      let { mesh, capacity } = this.instanceMeshes.get(key) ?? { mesh: undefined, capacity: 0 };

      // Create new InstancedMesh if needed
      if (!mesh || capacity < numInstances) {
        if (mesh) {
          mesh.dispose();
          mesh.removeFromParent();
        }
        mesh = new InstancedMesh(
          data.geometry,
          data.material,
          numInstances
        );
        mesh.userData.isAutoInstance = true;

        // Per-instance culling not supported
        mesh.frustumCulled = false;

        // Copy shadow properties from the first object (assuming all objects in the batch have the same shadow settings)
        const firstObj = data.objects[0];
        mesh.castShadow = firstObj.castShadow;
        mesh.receiveShadow = firstObj.receiveShadow;

        this.instanceMeshes.set(key, { mesh, capacity: numInstances });
        scene.add(mesh);
      }

      // Update instance count if needed
      if (mesh.count !== numInstances) {
        mesh.count = numInstances;
      }

      // Update instance transforms
      let instanceIndex = 0;
      for (const obj of data.objects) {
        if (obj instanceof THREE.InstancedMesh) {
          for (let index = 0; index < obj.count; ++index) {
            obj.getMatrixAt(index, this.tempMatrix);
            mesh.setMatrixAt(instanceIndex, this.tempMatrix.premultiply(obj.matrixWorld));
            if (obj.instanceColor) {
              obj.getColorAt(index, this.tempColor);
              mesh.setColorAt(instanceIndex, this.tempColor);
            }
            instanceIndex += 1;
          }

        } else {
          // Set instance matrix
          mesh.setMatrixAt(instanceIndex, obj.matrixWorld);

          // Optional: set per-instance color
          if (obj.material instanceof THREE.MeshBasicMaterial && mesh.instanceColor) {
            mesh.setColorAt(instanceIndex, obj.material.color);
          }
          instanceIndex += 1;
        }

        // Hide original object
        obj.visible = false;
      }

      // Mark matrices as needing update
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) {
        mesh.instanceColor.needsUpdate = true;
      }

      // Show the instanced mesh
      mesh.visible = true;
    }
    return instances;
  }

  @THREE.profile
  public override render(scene: THREE.Scene, camera: THREE.Camera): void {
    super.render(scene, camera);
  }
}
